import { getNavigationItems } from "../../utils/navigation";
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useVisibility } from "../../contexts/VisibilityContext";

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { layoutMode, setLayoutMode } = useTheme();
  const { canAccess } = useVisibility();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showPasswordResetNotice, setShowPasswordResetNotice] = useState(false);
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("navViewMode") || "simple",
  );
  const navbarDropdownRef = useRef(null);
  const sidebarDropdownRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("navViewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (!showProfileDropdown) return;
    const handleOutsideClick = (e) => {
      const insideNavbar = navbarDropdownRef.current?.contains(e.target);
      const insideSidebar = sidebarDropdownRef.current?.contains(e.target);
      if (!insideNavbar && !insideSidebar) setShowProfileDropdown(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showProfileDropdown]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setShowProfileDropdown(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showMobileMenu]);

  useEffect(() => {
    if (showMobileMenu) {
      setShowProfileDropdown(false);
    }
  }, [showMobileMenu]);

  useEffect(() => {
    if (!user?.id || !user?.forcePasswordReset) {
      setShowPasswordResetNotice(false);
      return;
    }

    const noticeKey = `password-reset-reminder-seen-${user.id}`;
    if (!sessionStorage.getItem(noticeKey)) {
      setShowPasswordResetNotice(true);
      sessionStorage.setItem(noticeKey, "true");
    }
  }, [user]);

  const isAdmin = () => {
    const adminCheck = user?.roles?.some(
      (role) => normalizeRole(role) === "ADMIN",
    );
    return adminCheck;
  };

  const normalizeRole = (role) => {
    const raw = typeof role === "string" ? role : role?.name || role?.authority;
    if (!raw || typeof raw !== "string") return "";
    return raw.toUpperCase().replace(/^ROLE_/, "");
  };

  const isBerufsbildner = () => {
    const berufsbildnerCheck = user?.roles?.some(
      (role) => normalizeRole(role) === "BERUFSBILDNER",
    );
    return berufsbildnerCheck;
  };

  const isPracticeInstructor = () => {
    return user?.roles?.some((role) => {
      const normalized = normalizeRole(role);
      return (
        normalized === "PRACTICE_INSTRUCTOR" || normalized === "PRAXISBILDNER"
      );
    });
  };


  const getThemeColors = () => {
    return {
      activeBg: "bg-gradient-to-r from-sunrise-red to-red-600",
      activeText: "text-white",
      inactiveHoverText: "hover:text-sunrise-red",
      inactiveHoverBg: "hover:bg-red-50",
      iconInactive: "text-gray-400 group-hover:text-sunrise-red",
      avatarBg: "bg-gradient-to-br from-sunrise-red to-red-600",
      badgeBg: "bg-red-100",
      badgeText: "text-red-800",
    };
  };

  const themeColors = getThemeColors();


  const isActive = (path) => {
    return location.pathname === path;
  };

  const navigationItems = getNavigationItems(user, viewMode, canAccess);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true, state: null });
  };

  const getInitials = () => {
    if (!user) return "U";
    const firstInitial = user.firstName
      ? user.firstName.charAt(0).toUpperCase()
      : "";
    const lastInitial = user.lastName
      ? user.lastName.charAt(0).toUpperCase()
      : "";
    return `${firstInitial}${lastInitial}` || "U";
  };

  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const closeMobileMenu = () => {
    setShowMobileMenu(false);
  };

  return (
    <>
      <style>{`
        @keyframes nav-dropdown-down {
          from { opacity: 0; transform: translateY(-6px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)  scale(1); }
        }
        @keyframes nav-dropdown-up {
          from { opacity: 0; transform: translateY(6px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      {showPasswordResetNotice && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-brand-xl border border-pale-dawn">
            <div className="border-b border-amber-100 bg-amber-50 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                  <i className="fas fa-key"></i>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-grey-dusk">
                    Please change your password
                  </h2>
                  <p className="text-sm text-amber-900/80">
                    You are logged in normally, but your password should be
                    updated.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-5 py-4">
              <p className="text-sm leading-6 text-gray-600">
                Click your name, open{" "}
                <span className="font-semibold text-grey-dusk">My Profile</span>
                , and change your password there.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-4">
              <button
                type="button"
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                onClick={() => setShowPasswordResetNotice(false)}
              >
                Later
              </button>
              <Link
                to="/profile"
                className="rounded-lg bg-sunrise-red px-4 py-2 text-sm font-semibold text-white hover:bg-sunrise-600"
                onClick={() => setShowPasswordResetNotice(false)}
              >
                Open My Profile
              </Link>
            </div>
          </div>
        </div>
      )}

      {layoutMode === "sidebar" && (
        <aside className="hidden md:flex flex-col fixed left-0 top-0 h-full w-[280px] bg-white border-r border-gray-200 z-[60] font-sans shadow-lg transition-all duration-300">
          <div className="p-6 border-b border-gray-100 flex justify-center">
            <Link to="/">
              <img
                src="/Journey_Logo.png"
                alt="Journey Logo"
                className="h-16 w-auto object-contain hover:scale-105 transition-transform duration-200"
              />
            </Link>
          </div>

          <div className="flex-1 py-6 px-3 space-y-2 overflow-y-auto">
            {navigationItems.map((item, index) => (
              <Link
                key={index}
                to={item.to}
                title={item.label}
                className={`group flex items-center px-3.5 py-3.5 rounded-xl font-medium transition-all duration-200 ${
                  isActive(item.to)
                    ? `${themeColors.activeText} ${themeColors.activeBg} shadow-md transform scale-[1.02]`
                    : `text-gray-600 ${themeColors.inactiveHoverText} ${themeColors.inactiveHoverBg}`
                }`}
              >
                {item.icon.includes("/") || item.icon.includes(".") ? (
                  <img
                    src={item.icon}
                    alt={item.label}
                    className={`w-6 h-6 shrink-0 object-contain mr-2.5 transition-transform group-hover:scale-110 ${
                      isActive(item.to)
                        ? ""
                        : "opacity-70 group-hover:opacity-100"
                    }`}
                  />
                ) : (
                  <i
                    className={`fas ${item.icon} w-6 shrink-0 text-center mr-2.5 text-lg transition-transform group-hover:scale-110 ${
                      isActive(item.to)
                        ? themeColors.activeText
                        : themeColors.iconInactive
                    }`}
                  ></i>
                )}
                {/* Long labels ("Learning Documentation") get the full row width
                    and stay on one line; the tooltip covers the rare overflow. */}
                <span className="flex-1 min-w-0 text-sm leading-snug truncate">
                  {item.label}
                </span>
                {isActive(item.to) && (
                  <i className="fas fa-chevron-right text-xs opacity-75 ml-2 shrink-0"></i>
                )}
              </Link>
            ))}
          </div>

          <div className="p-4 border-t border-gray-100 bg-gray-50/50">
            <div className="flex items-center justify-between mb-4 bg-white/50 p-2 rounded-lg border border-gray-100 shadow-sm">
              <span className="text-xs font-semibold tracking-wider text-gray-400 pl-2">
                Layout
              </span>
              <div className="flex bg-gray-100/80 rounded-full p-1 border border-gray-200/50">
                <button
                  onClick={() => setLayoutMode("navbar")}
                  className="w-8 h-6 rounded-full text-[10px] font-semibold transition-all duration-200 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-white/50"
                  title="Switch to Top Navigation"
                >
                  <i className="fas fa-bars"></i>
                </button>
                <button
                  onClick={() => setLayoutMode("sidebar")}
                  className="w-8 h-6 rounded-full text-[10px] font-semibold transition-all duration-200 flex items-center justify-center bg-white text-sunrise-red shadow-sm transform scale-105"
                  title="Sidebar Active"
                >
                  <i className="fas fa-columns"></i>
                </button>
              </div>
            </div>

            <div className="relative" ref={sidebarDropdownRef}>
              <button
                onClick={toggleProfileDropdown}
                className={`w-full flex items-center p-3 rounded-xl transition-all duration-200 border ${
                  showProfileDropdown
                    ? `bg-white ${
                        isPracticeInstructor()
                          ? "border-yellow-500/20"
                          : "border-sunrise-red/20"
                      } shadow-md`
                    : "hover:bg-white border-transparent hover:border-gray-200 hover:shadow-sm"
                }`}
              >
                <div className="relative">
                  <div
                    className={`w-10 h-10 ${themeColors.avatarBg} text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm`}
                  >
                    {getInitials()}
                  </div>
                  {user?.professionType && (
                    <div
                      className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center shadow-sm ${
                        user.professionType === "INFORMATIKER" ||
                        user.professionType === "INFORMATIK"
                          ? "bg-blue-100 text-blue-600"
                          : user.professionType === "MEDIAMATIKER" ||
                              user.professionType === "MEDIAMATIK"
                            ? "bg-purple-100 text-purple-600"
                            : user.professionType === "KV"
                              ? "bg-gray-100 text-gray-600"
                              : user.professionType === "KUNDENDIALOG"
                                ? "bg-emerald-100 text-emerald-600"
                                : user.professionType === "DETAILHANDEL"
                                  ? "bg-orange-100 text-orange-600"
                                  : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      <i
                        className={`${
                          user.professionType === "INFORMATIKER" ||
                          user.professionType === "INFORMATIK"
                            ? "fas fa-laptop-code"
                            : user.professionType === "MEDIAMATIKER" ||
                                user.professionType === "MEDIAMATIK"
                              ? "fas fa-palette"
                              : user.professionType === "KV"
                                ? "fas fa-file-invoice"
                                : user.professionType === "KUNDENDIALOG"
                                  ? "fas fa-headset"
                                  : user.professionType === "DETAILHANDEL"
                                    ? "fas fa-store"
                                    : "fas fa-user"
                        } text-[8px]`}
                      ></i>
                    </div>
                  )}
                </div>

                <div className="ml-3 text-left overflow-hidden flex-1">
                  <p className="text-sm font-bold text-gray-800 truncate">
                    {user?.firstName}
                  </p>
                  <p className="text-xs text-gray-500 truncate font-medium">
                    {user?.email}
                  </p>
                </div>

                <i
                  className={`fas fa-chevron-up text-xs text-gray-400 ml-2 transition-transform duration-200 ${
                    showProfileDropdown ? "" : "rotate-180"
                  }`}
                ></i>
              </button>

              {showProfileDropdown && (
                <div className="absolute bottom-full left-0 w-full mb-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50" style={{ animation: "nav-dropdown-up 0.13s cubic-bezier(0.16,1,0.3,1) forwards" }}>
                  {/* User identity */}
                  <div className="px-4 py-3 flex items-center gap-3 border-b border-gray-100 bg-gray-50/60">
                    <div className={`w-9 h-9 ${themeColors.avatarBg} text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm flex-shrink-0`}>
                      {getInitials()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate leading-tight">{user?.firstName} {user?.lastName}</p>
                      <p className="text-[11px] text-gray-400 truncate">{user?.email}</p>
                    </div>
                  </div>

                  {/* Expert mode */}
                  {(isAdmin() || isBerufsbildner()) && (
                    <div className="px-4 py-2.5 border-b border-gray-100">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold text-gray-700">Expert Mode</p>
                          <p className="text-[10px] text-gray-400">{viewMode === "expert" ? "All options visible" : "Simplified menu"}</p>
                        </div>
                        <button
                          onClick={() => setViewMode(viewMode === "expert" ? "simple" : "expert")}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200 ${viewMode === "expert" ? "bg-sunrise-red" : "bg-gray-200"}`}
                        >
                          <span className={`inline-block h-4 w-4 m-0.5 rounded-full bg-white shadow transition-transform duration-200 ${viewMode === "expert" ? "translate-x-4" : "translate-x-0"}`} />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="py-1">
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setShowProfileDropdown(false)}
                    >
                      <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-user text-gray-500 text-[11px]"></i>
                      </div>
                      <span className="font-medium">My Profile</span>
                    </Link>
                  </div>

                  <div className="border-t border-gray-100 py-1">
                    <button
                      onClick={() => { handleLogout(); setShowProfileDropdown(false); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-sign-out-alt text-red-400 text-[11px]"></i>
                      </div>
                      <span className="font-medium">Sign out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>
      )}

      <nav
        className={`sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm ${
          layoutMode === "sidebar" ? "md:hidden" : ""
        }`}
      >
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="/Journey_Logo.png"
                alt="Journey Logo"
                className="h-8 w-auto object-contain"
              />
            </Link>

            {layoutMode === "navbar" && (
              <>
                <div className="hidden md:flex items-center space-x-2 flex-1 ml-6 overflow-x-auto">
                  {navigationItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.to}
                      className={`flex items-center text-sm font-medium transition-all duration-200 px-3 py-2 rounded-lg whitespace-nowrap ${
                        isActive(item.to)
                          ? "text-sunrise-red bg-sunrise-red/5"
                          : "text-gray-600 hover:text-sunrise-red hover:bg-gray-50"
                      }`}
                    >
                      {item.icon.includes("/") || item.icon.includes(".") ? (
                        <img
                          src={item.icon}
                          alt={item.label}
                          className="w-4 h-4 object-contain mr-2"
                        />
                      ) : (
                        <i className={`fas ${item.icon} mr-2 text-xs`}></i>
                      )}
                      {item.label}
                    </Link>
                  ))}
                </div>

                <div className="hidden md:flex items-center space-x-4 ml-auto pl-4">
                  <div className="hidden md:flex items-center bg-gray-100 rounded-full p-1 border border-gray-200">
                    <button
                      onClick={() => setLayoutMode("navbar")}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 flex items-center ${
                        layoutMode === "navbar"
                          ? "bg-white text-sunrise-red shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <i className="fas fa-bars mr-1.5"></i>
                      Top
                    </button>
                    <button
                      onClick={() => setLayoutMode("sidebar")}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 flex items-center ${
                        layoutMode === "sidebar"
                          ? "bg-white text-sunrise-red shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <i className="fas fa-columns mr-1.5"></i>
                      Side
                    </button>
                  </div>

                  <div className="relative z-50" ref={navbarDropdownRef}>
                    <button
                      onClick={toggleProfileDropdown}
                      className="flex items-center space-x-2 p-1.5 rounded-full hover:bg-gray-50 transition-all duration-200 border border-transparent hover:border-gray-200"
                    >
                      <div className="relative">
                        <div className="w-9 h-9 bg-gradient-to-br from-sunrise-red to-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm">
                          {getInitials()}
                        </div>
                        {user?.professionType && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white flex items-center justify-center p-0.5 shadow-sm">
                            <div
                              className={`w-full h-full rounded-full flex items-center justify-center ${
                                user.professionType.includes("INFORMATIK")
                                  ? "bg-blue-100 text-blue-600"
                                  : user.professionType.includes("MEDIA")
                                    ? "bg-purple-100 text-purple-600"
                                    : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              <i className="fas fa-coins text-[6px]"></i>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="text-left hidden lg:block">
                        <p className="text-sm font-semibold text-gray-700 leading-none">
                          {user?.firstName}
                        </p>
                        <p className="text-[10px] text-gray-400 font-medium leading-tight mt-1">
                          {isAdmin()
                            ? "Administrator"
                            : isBerufsbildner()
                              ? "Trainer"
                              : "Student"}
                        </p>
                      </div>

                      <i
                        className={`fas fa-chevron-down text-xs text-gray-400 px-1 transition-transform duration-200 ${
                          showProfileDropdown ? "rotate-180" : ""
                        }`}
                      ></i>
                    </button>

                    {showProfileDropdown && (
                      <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[70]" style={{ animation: "nav-dropdown-down 0.13s cubic-bezier(0.16,1,0.3,1) forwards" }}>
                        {/* User identity header */}
                        <div className="px-4 py-3.5 flex items-center gap-3 border-b border-gray-100 bg-gray-50/60">
                          <div className="w-10 h-10 bg-gradient-to-br from-sunrise-red to-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm flex-shrink-0">
                            {getInitials()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900 truncate leading-tight">{user?.firstName} {user?.lastName}</p>
                            <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                          </div>
                        </div>

                        {/* Expert mode */}
                        {(isAdmin() || isBerufsbildner()) && (
                          <div className="px-4 py-2.5 border-b border-gray-100">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <p className="text-xs font-semibold text-gray-700">Expert Mode</p>
                                <p className="text-[10px] text-gray-400">{viewMode === "expert" ? "All options visible" : "Simplified menu"}</p>
                              </div>
                              <button
                                onClick={() => setViewMode(viewMode === "expert" ? "simple" : "expert")}
                                className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200 ${viewMode === "expert" ? "bg-sunrise-red" : "bg-gray-200"}`}
                              >
                                <span className={`inline-block h-4 w-4 m-0.5 rounded-full bg-white shadow transition-transform duration-200 ${viewMode === "expert" ? "translate-x-4" : "translate-x-0"}`} />
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="py-1">
                          <Link
                            to="/profile"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            onClick={() => setShowProfileDropdown(false)}
                          >
                            <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <i className="fas fa-user text-gray-500 text-[11px]"></i>
                            </div>
                            <span className="font-medium">My Profile</span>
                          </Link>
                        </div>

                        <div className="border-t border-gray-100 py-1">
                          <button
                            onClick={() => { handleLogout(); setShowProfileDropdown(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                              <i className="fas fa-sign-out-alt text-red-400 text-[11px]"></i>
                            </div>
                            <span className="font-medium">Sign out</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="md:hidden flex-shrink-0">
              <button
                onClick={toggleMobileMenu}
                className="w-10 h-10 rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm flex items-center justify-center"
                aria-label={
                  showMobileMenu
                    ? "Close navigation menu"
                    : "Open navigation menu"
                }
              >
                <i
                  className={`fas ${showMobileMenu ? "fa-times" : "fa-bars"} text-lg`}
                ></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {showMobileMenu && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/45 md:hidden"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />

          <div className="fixed inset-y-0 right-0 z-[70] w-[320px] max-w-[88vw] bg-white shadow-2xl md:hidden flex flex-col">
            <div className="flex items-center justify-between px-4 h-16 border-b border-gray-200">
              <div className="flex items-center space-x-3 min-w-0">
                <div className="w-10 h-10 bg-gradient-to-br from-sunrise-red to-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {getInitials()}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-gray-800 truncate">
                    {user?.firstName}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {user?.email}
                  </div>
                </div>
              </div>

              <button
                onClick={closeMobileMenu}
                className="w-10 h-10 rounded-xl border border-gray-200 bg-white text-gray-700 flex items-center justify-center"
                aria-label="Close menu"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-4">
              <div className="space-y-1">
                {navigationItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.to}
                    onClick={closeMobileMenu}
                    className={`flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      isActive(item.to)
                        ? "bg-sunrise-red/10 text-sunrise-red"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {item.icon.includes("/") || item.icon.includes(".") ? (
                      <img
                        src={item.icon}
                        alt={item.label}
                        className="w-5 h-5 object-contain mr-3"
                      />
                    ) : (
                      <i
                        className={`fas ${item.icon} w-5 mr-3 text-center`}
                      ></i>
                    )}

                    <span className="flex-1">{item.label}</span>

                    {isActive(item.to) && (
                      <i className="fas fa-chevron-right text-xs"></i>
                    )}
                  </Link>
                ))}
              </div>

              {(isAdmin() || isBerufsbildner()) && (
                <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Expert View
                    </span>
                    <button
                      onClick={() =>
                        setViewMode(viewMode === "expert" ? "simple" : "expert")
                      }
                      className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
                        viewMode === "expert" ? "bg-sunrise-red" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition duration-200 ${
                          viewMode === "expert"
                            ? "translate-x-5"
                            : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 p-3 space-y-1">
              <Link
                to="/profile"
                onClick={closeMobileMenu}
                className="flex items-center px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <i className="fas fa-user w-5 mr-3 text-center"></i>
                <span className="flex-1">My Profile</span>
              </Link>

              <button
                onClick={() => {
                  handleLogout();
                  closeMobileMenu();
                }}
                className="w-full flex items-center px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <i className="fas fa-sign-out-alt w-5 mr-3 text-center"></i>
                <span className="flex-1 text-left">Logout</span>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Navigation;
